import { useSelector } from '@common/performance/useSelector';
import {
  ComponentInfo,
  GemSpec,
  GemType,
  BaseState,
  CommonGraph,
  CommonState,
  GenericGraphProcessType,
  Metadata
} from '@common/types';
import { useBaseLSPClient } from '@LSP/base/BaseLSPContext';
import { FROZEN_EMPTY_ARRAY, FROZEN_EMPTY_OBJECT } from '@prophecy/utils/constants';
import { useEffect, useState } from 'react';
import { EqualityFn, shallowEqual, useSelector as useReduxSelector } from 'react-redux';

import { WorkflowModeType } from '../graphqlTypes/enums';
import { getCurrentProcessGraphPath, getGraphProcess, getGraphProperty } from '../Parser/bindings';
import { Port } from '../redux/portTypes';
import { WorkflowGraph } from '../redux/types';
import { getGemSpec } from './getGemSpec';
import { getGemTypeByProcess } from './getGemTypeByProcess';

type UseGraphMetaInfoOptions<G extends CommonGraph, T> = {
  equalityFn?: EqualityFn<T>;
  defaultMetaInfo?: G['metainfo'];
};

export function useGraphMetaInfo<G extends CommonGraph, Selected>(
  selector: (value: G['metainfo']) => Selected,
  options?: UseGraphMetaInfoOptions<G, Selected>
): Selected;

export function useGraphMetaInfo<G extends CommonGraph>(): G['metainfo'];

export function useGraphMetaInfo<G extends CommonGraph, Selected>(
  selector = (metainfo: G['metainfo']) => metainfo,
  options?: UseGraphMetaInfoOptions<G, Selected>
) {
  return useSelector<BaseState<G, Metadata>>(
    (state) => selector(state.currentGraph?.metainfo || options?.defaultMetaInfo || FROZEN_EMPTY_OBJECT),
    {
      equalityFn: options?.equalityFn as EqualityFn<unknown>
    }
  );
}

export function useWorkflowMode() {
  return useSelector<BaseState<WorkflowGraph, Metadata>, WorkflowModeType | undefined>(
    (state) => state.currentGraph?.metainfo.mode
  );
}

/**
 * Note: We might not get spec of a component if the component is deleted. so return of this can be undefined.
 */
export function useComponentSpec<G extends CommonGraph>(gemType: GemType) {
  return useSelector<BaseState<G>, GemSpec | undefined>((state) => {
    return getGemSpec(state.gemSpecs, gemType);
  });
}

export function useCurrentProcess<T = GenericGraphProcessType<CommonGraph> | undefined>(
  processSelector: (process: GenericGraphProcessType<CommonGraph>) => T = (process) => process as unknown as T,
  equalityFn: (a: T | undefined, b: T | undefined) => boolean = shallowEqual
) {
  const processInfo = useSelector<CommonState, T | undefined>((state) => {
    const dialogProcess = dialogProcessSelector(state);
    if (!dialogProcess) {
      return undefined;
    }
    return processSelector(dialogProcess);
  }, equalityFn);

  return processInfo;
}

export const useInputPorts = () => {
  return useCurrentProcess((process) => process?.ports?.inputs || (FROZEN_EMPTY_ARRAY as Port[]));
};

export function useProcessDialogOpenState() {
  // Using redux selector to re-render when the state changes even when the state update is disabled
  const componentId = useReduxSelector<CommonState, string | undefined>((state) => state.currentComponentId);
  const processSpecDialogOpen = useReduxSelector<CommonState, boolean | undefined>(
    (state) => state.processSpecDialogOpen
  );
  const graphPath = useReduxSelector<CommonState, string | undefined>((state) => state.graphPath);

  return { open: Boolean(processSpecDialogOpen && componentId), componentId, graphPath };
}

export function gemTypeToString(gemType: GemType) {
  if (gemType.componentInfo) {
    return gemType.componentInfo?.gemId;
  }
  return gemType.component;
}
export function gemSpecsToGemTypeString(gemSpecs: CommonState['gemSpecs']) {
  const gemTypes = gemSpecs.map((spec) => gemTypeToString(gemSpecToGemType(spec)));
  return gemTypes;
}
export function gemSpecToGemType(gemSpec: GemSpec): GemType {
  return {
    componentInfo: gemSpec.componentInfo ? componentInfoToProcessComponentInfo(gemSpec.componentInfo) : undefined,
    component: gemSpec.kind,
    category: gemSpec.category,
    isTransformationGem: gemSpec.isTransformationGem
  };
}

export function getGemTypeByKind<K extends string>(gemSpecs: CommonState['gemSpecs'], kind: K) {
  const spec = gemSpecs.find((spec) => spec.kind === kind);
  if (spec) {
    return gemSpecToGemType(spec);
  }
}
export function getGemSpecByKind<K extends string>(gemSpecs: CommonState['gemSpecs'], kind: K) {
  const spec = gemSpecs.find((spec) => spec.kind === kind);
  return spec;
}
function componentInfoToProcessComponentInfo(componentInfo: ComponentInfo): ComponentInfo {
  return componentInfo;
}
export function useGraphMetaInfoOriginalState<G extends CommonGraph>() {
  return useSelector<BaseState<G, Metadata>, G['metainfo']>((state) => state.$graph?.metainfo);
}

export function useCompilationInProgress() {
  const lspClient = useBaseLSPClient();
  const [isCompilationInProgress, setIsCompilationInProgress] = useState(false);

  useEffect(() => {
    return lspClient?.listenRequestCount((count) => setIsCompilationInProgress(count > 0));
  }, [lspClient]);

  return isCompilationInProgress;
}

export function originalGraphSelector<G extends CommonGraph, S extends BaseState<G, Metadata>>(state: S) {
  const rootGraph = originalRootGraphSelector(state);
  const rootPath = rootPathSelector(state);
  const graphPath = graphPathSelector(state);

  return getCurrentGraph(rootGraph, rootPath, graphPath) as GetCurrentGraphResult<G>;
}

export function currentGraphSelector<G extends CommonGraph, S extends BaseState<G, Metadata>>(
  state: S
): GetCurrentGraphResult<G> {
  const rootGraph = currentRootGraphSelector(state);
  const rootPath = rootPathSelector(state);
  const graphPath = graphPathSelector(state);

  return getCurrentGraph(rootGraph, rootPath, graphPath) as GetCurrentGraphResult<G>;
}
export function currentDialogGraphSelector<G extends CommonGraph, S extends BaseState<G, Metadata>>(
  state: S
): GetCurrentGraphResult<G> {
  const rootGraph = currentRootGraphSelector(state);
  const rootPath = rootPathSelector(state);
  const graphPath = graphPathSelector(state);
  const processId = dialogProcessIdSelector(state);

  const currentProcessGraphPath = getCurrentProcessGraphPath(processId, graphPath);

  return getCurrentGraph(rootGraph, rootPath, currentProcessGraphPath) as GetCurrentGraphResult<G>;
}

export type GraphStates<G extends CommonGraph> = { wipState: G; savedState: G };
export function graphStatesSelector<G extends CommonGraph, S extends BaseState<G, Metadata>>(state: S): GraphStates<G> {
  const wipState = currentRootGraphSelector(state) as G;
  const savedState = state.$graph as G;
  return { wipState, savedState };
}

export function dialogProcessSelector<G extends CommonGraph, S extends BaseState<G, Metadata>>(
  state: S
): GenericGraphProcessType<CommonGraph> | undefined {
  const processId = dialogProcessIdSelector(state);
  if (!processId) {
    return undefined;
  }
  const process = currentGraphProcessSelector(state, processId);
  return process;
}

export function dialogProcessIdSelector<G extends CommonGraph, S extends BaseState<G, Metadata>>(state: S) {
  const { currentComponentId } = state;
  return currentComponentId;
}

export function originalRootGraphSelector<S extends CommonState>(state: S) {
  return state.$graph!;
}
export function currentRootGraphSelector<S extends CommonState>(state: S) {
  return state.currentGraph!;
}
export function rootPathSelector<G extends CommonGraph, S extends BaseState<G, Metadata>>(state: S) {
  return state.root;
}
export function graphPathSelector<G extends CommonGraph, S extends BaseState<G, Metadata>>(state: S) {
  return state.graphPath;
}

export function dialogProcessGemTypeSelector<G extends CommonGraph, S extends BaseState<G, Metadata>>(state: S) {
  const process = dialogProcessSelector(state);
  return process ? getGemTypeByProcess(process) : undefined;
}
export function currentGraphProcessSelector<G extends CommonGraph, S extends BaseState<G, Metadata>>(
  state: S,
  processId: string
) {
  const { currentGraph: rootGraph } = state;
  if (!rootGraph) {
    return undefined;
  }
  const graphPath = graphPathSelector(state);
  const rootPath = rootPathSelector(state);

  const currentProcessGraphPath = getCurrentProcessGraphPath(processId, graphPath);
  const process = getGraphProcess(rootGraph, currentProcessGraphPath, processId, rootPath);
  return process;
}

export type GetCurrentGraphResult<G extends CommonGraph> = {
  rootGraph: G;
  rootPath: string;
  graph: G;
  graphPath: string;
};

export function getCurrentGraph<G extends CommonGraph>(
  rootGraph: G,
  rootPath: string,
  graphPath: string
): GetCurrentGraphResult<G> {
  const graph = rootPath !== graphPath ? (getGraphProperty(rootGraph, graphPath, rootPath) as G) : rootGraph;

  return { rootGraph, rootPath, graph, graphPath };
}
